import { XClose } from "@untitledui/icons";
import type React from "react";
import { useEffect } from "react";
import {
	Button,
	type Props as ButtonProps,
} from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";

interface ModalAction {
	label: string;
	onClick?: () => void;
	color?: ButtonProps["color"];
	disabled?: boolean;
}

interface ModalBaseProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	description?: string;
	children?: React.ReactNode;
	icon?: React.ReactNode;
	iconColor?: "brand" | "success" | "warning" | "gray" | "error";
	actions?: ModalAction[];
	width?: string | number;
	height?: string | number;
}

export const ModalBase: React.FC<ModalBaseProps> = ({
	isOpen,
	onClose,
	title,
	description,
	children,
	icon,
	iconColor = "brand",
	actions,
	width = "100%",
	height = "auto",
}) => {
	useEffect(() => {
		if (isOpen) document.body.style.overflow = "hidden";
		else document.body.style.overflow = "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [isOpen]);

	if (!isOpen) return null;

	const sizeStyle = {
		width: typeof width === "number" ? `${width}px` : width,
		height: typeof height === "number" ? `${height}px` : height,
		maxWidth: "90vw",
		maxHeight: "90vh",
	};

	const renderIcon = () => (
		<div
			className={`z-[-1] flex justify-center sm:justify-start ${icon ? "mb-4" : ""}`}
		>
			<div className="pointer-events-none relative flex items-center justify-center">
				{icon && <FeaturedIcon color={iconColor} icon={icon} size="lg" />}
				{[20, 28, 36, 44, 52].map((size) => (
					<div
						className="absolute rounded-full border border-gray-200"
						key={size}
						style={{
							height: `${size * 4}px`,
							width: `${size * 4}px`,
							opacity: (70 - size / 2) / 100,
						}}
					/>
				))}
			</div>
		</div>
	);

	const renderActions = () => (
		<div className="flex flex-col gap-3 sm:flex-row sm:gap-2">
			{actions?.map((action) => (
				<Button
					className="h-11 flex-1"
					color={action.color ?? "primary"}
					isDisabled={action.disabled}
					key={action.label}
					onClick={action.onClick ?? onClose}
				>
					{action.label}
				</Button>
			))}
		</div>
	);

	return (
		<div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-sm">
			<div
				className="relative z-10 flex max-h-[90vh] max-w-full flex-col overflow-hidden rounded-xl bg-white p-6 shadow-xl sm:p-4 md:p-6"
				data-testid="modal-container"
				style={sizeStyle}
			>
				<button
					aria-label="Fechar modal"
					className="absolute top-4 right-4 cursor-pointer text-gray-400 hover:text-gray-600"
					onClick={onClose}
				>
					<XClose className="size-6" />
				</button>

				{renderIcon()}

				{title && (
					<h1 className="mb-2 font-semibold text-gray-900 text-lg sm:text-base md:text-lg">
						{title}
					</h1>
				)}

				{description && (
					<p className="mb-4 text-gray-600 text-sm sm:text-xs md:text-sm">
						{description}
					</p>
				)}

				<div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

				{actions && renderActions()}
			</div>
		</div>
	);
};

export default ModalBase;
